import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../services/api';
import { Loading } from './ui';
import { formatarMoeda } from '../lib/format';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown } from 'lucide-react';

const nomesMes = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

interface Grupo {
  realizado: number;
  previsto: number;
  porCategoria: Record<string, { realizado: number; previsto: number }>;
}

/* ══ Projeção de caixa do mês (receitas, custos e despesas) ══ */
export function ProjecaoCaixa() {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ['projecao-caixa', mes, ano],
    queryFn: () => dashboardApi.projecaoCaixa({ mes, ano }).then(r => r.data),
  });

  const navegar = (delta: number) => {
    const d = new Date(ano, mes - 1 + delta, 1);
    setMes(d.getMonth() + 1);
    setAno(d.getFullYear());
  };

  const totais = data?.totais;
  const projetadoPositivo = (totais?.saldoProjetado || 0) >= 0;

  return (
    <div className="table-shell">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-ink-100">
        <div>
          <h2 className="card-title">Projeção de Caixa</h2>
          <p className="text-xs text-ink-400 mt-0.5">
            Realizado (recebido/pago) e previsto (a receber/a pagar) por vencimento
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => navegar(-1)} className="icon-btn" title="Mês anterior">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-ink-800 w-32 text-center">
            {nomesMes[mes - 1]} {ano}
          </span>
          <button onClick={() => navegar(1)} className="icon-btn" title="Próximo mês">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th className="!text-right">Recebido / Pago</th>
                <th className="!text-right">A Receber / A Pagar</th>
                <th className="!text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <SecaoGrupo titulo="Receitas" grupo={data?.receitas} tom="emerald" />
              <SecaoGrupo titulo="Custos" grupo={data?.custos} tom="red" />
              <SecaoGrupo titulo="Despesas" grupo={data?.despesas} tom="red" />
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-ink-200 bg-ink-50/60">
                <td className="!py-3 font-semibold text-ink-900">Total</td>
                <td className="!py-3 !text-right">
                  <span className="block text-emerald-700 font-semibold">+ {formatarMoeda(totais?.recebido)}</span>
                  <span className="block text-red-600 font-semibold">− {formatarMoeda(totais?.pago)}</span>
                </td>
                <td className="!py-3 !text-right">
                  <span className="block text-emerald-700/70 font-medium">+ {formatarMoeda(totais?.aReceber)}</span>
                  <span className="block text-red-500/80 font-medium">− {formatarMoeda(totais?.aPagar)}</span>
                </td>
                <td className="!py-3 !text-right">
                  <span className="block text-xs text-ink-400">Realizado: {formatarMoeda(totais?.saldoRealizado)}</span>
                  <span className={`inline-flex items-center gap-1 font-display font-bold ${projetadoPositivo ? 'text-emerald-700' : 'text-red-600'}`}>
                    {projetadoPositivo ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {formatarMoeda(totais?.saldoProjetado)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function SecaoGrupo({ titulo, grupo, tom }: { titulo: string; grupo?: Grupo; tom: 'emerald' | 'red' }) {
  if (!grupo) return null;
  const categorias = Object.entries(grupo.porCategoria);
  const cor = tom === 'emerald' ? 'text-emerald-700' : 'text-red-600';

  return (
    <>
      <tr className="bg-ink-50/40">
        <td className="!py-2.5 text-2xs font-bold uppercase tracking-wider text-ink-500">{titulo}</td>
        <td className={`!py-2.5 !text-right font-semibold ${cor}`}>{formatarMoeda(grupo.realizado)}</td>
        <td className={`!py-2.5 !text-right font-medium ${cor} opacity-70`}>{formatarMoeda(grupo.previsto)}</td>
        <td className={`!py-2.5 !text-right font-bold ${cor}`}>{formatarMoeda(grupo.realizado + grupo.previsto)}</td>
      </tr>
      {categorias.length === 0 ? (
        <tr>
          <td colSpan={4} className="!py-2.5 pl-8 text-[13px] text-ink-400 italic">
            Nenhum lançamento de {titulo.toLowerCase()} no período.
          </td>
        </tr>
      ) : (
        categorias
          .sort((a, b) => (b[1].realizado + b[1].previsto) - (a[1].realizado + a[1].previsto))
          .map(([categoria, v]) => (
            <tr key={`${titulo}-${categoria}`}>
              <td className="pl-8 text-ink-700">{categoria}</td>
              <td className="!text-right text-ink-600">{v.realizado ? formatarMoeda(v.realizado) : '—'}</td>
              <td className="!text-right text-ink-400">{v.previsto ? formatarMoeda(v.previsto) : '—'}</td>
              <td className="!text-right font-medium text-ink-800">{formatarMoeda(v.realizado + v.previsto)}</td>
            </tr>
          ))
      )}
    </>
  );
}
